import { useEffect, useRef, useState } from "react";
import ChromePicker from "react-color/lib/components/chrome/Chrome";
import type { ColorResult } from "react-color";
import { useTranslation } from "react-i18next";
import { normalizeColor, parseColor } from "../../libs/simple/colors";

type SimpleSwatchProps = {
  slotKey: string;
  label: string;
  color: string;
  // Called while the user drags in the picker.
  onPreview(color: string): void;
  // Called once when the picker closes after previews. This is the undo step.
  onCommitPreview(): void;
  // Called instead when the picker is closed with Escape: the previews are
  // dropped, as a value typed into the hex field is.
  onCancelPreview(): void;
  // Called for a colour typed into the hex field. Also one undo step.
  onApply(color: string): void;
  // The user points at the row (hover or keyboard focus), or stops. Not
  // while the picker is open: then the whole map is what matters.
  onPoint?(pointing: boolean): void;
  // A colour that cannot be see-through: the picker has no opacity slider.
  solid?: boolean;
};

function fromPicker(result: ColorResult, solid: boolean): string {
  const { r, g, b, a } = result.rgb;
  const alpha = solid || a === undefined ? 1 : a;
  return normalizeColor(`rgba(${r}, ${g}, ${b}, ${alpha})`);
}

// A picker opened low in the panel would otherwise sit below the fold.
const reveal = (picker: HTMLDivElement | null) => {
  picker?.scrollIntoView({ block: "nearest" });
};

export default function SimpleSwatch(props: SimpleSwatchProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);

  // True once the picker has previewed something that is not committed yet.
  const previewed = useRef(false);
  // True after Escape, until the blur that follows it. That blur can arrive
  // before the cleared draft has rendered, and must not commit it.
  const abandoned = useRef(false);
  // Always the newest props, for the handlers that run late.
  const latest = useRef(props);
  latest.current = props;

  const closePicker = (how: "commit" | "cancel" = "commit") => {
    if (previewed.current) {
      previewed.current = false;
      if (how === "commit") latest.current.onCommitPreview();
      else latest.current.onCancelPreview();
    }
    setOpen(false);
  };

  // Commit if the swatch disappears with the picker still open. The panel
  // refuses the commit when the style changed underneath in the meantime.
  useEffect(() => {
    return () => {
      if (previewed.current) {
        previewed.current = false;
        latest.current.onCommitPreview();
      }
    };
  }, []);

  // Escape closes the picker wherever the focus is, and drops what it
  // previewed. Dragging in the picker moves the focus out of the swatch, so a
  // handler on the swatch misses it.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closePicker("cancel");
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
    // closePicker only reads refs and the state setter, so it is safe to capture.
  }, [open]);

  // A colour that changed from outside (undo, another style) makes a
  // half-typed value meaningless.
  useEffect(() => {
    setDraft(null);
  }, [props.color]);

  const commitDraft = () => {
    if (abandoned.current) {
      abandoned.current = false;
      setDraft(null);
      return;
    }
    const typed = draft !== null && parseColor(draft) ? normalizeColor(draft) : null;
    setDraft(null);
    if (typed !== null && typed !== normalizeColor(props.color)) {
      props.onApply(typed);
    }
  };

  return (
    <div
      className="studio-swatch"
      data-wd-key={`simple:slot:${props.slotKey}`}
      onMouseEnter={() => !open && props.onPoint?.(true)}
      onMouseLeave={() => props.onPoint?.(false)}
      onFocus={() => !open && props.onPoint?.(true)}
      onBlur={() => props.onPoint?.(false)}
    >
      <button
        type="button"
        className="studio-swatch__chip"
        style={{ backgroundColor: props.color }}
        aria-label={t("Pick colour: {{name}}", { name: props.label })}
        aria-expanded={open}
        data-wd-key={`simple:slot:${props.slotKey}:chip`}
        onClick={() => {
          if (open) {
            closePicker();
          } else {
            props.onPoint?.(false);
            setOpen(true);
          }
        }}
      />
      <span className="studio-swatch__label" title={props.label}>
        {props.label}
      </span>
      <input
        className="studio-swatch__hex"
        dir="ltr"
        spellCheck={false}
        autoComplete="off"
        aria-label={t("Colour value: {{name}}", { name: props.label })}
        data-wd-key={`simple:slot:${props.slotKey}:hex`}
        value={draft !== null ? draft : props.color}
        onChange={(e) => {
          abandoned.current = false;
          setDraft(e.target.value);
        }}
        onBlur={commitDraft}
        onKeyDown={(e) => {
          if (e.key === "Enter") commitDraft();
          // Escape abandons the typed value. Clearing the draft here means
          // the blur the app triggers on Escape has nothing left to commit.
          if (e.key === "Escape") {
            abandoned.current = true;
            setDraft(null);
          }
        }}
      />
      {open && (
        <>
          <div
            className="studio-swatch__backdrop"
            onClick={() => closePicker()}
          />
          <div className="studio-swatch__picker" ref={reveal}>
            <ChromePicker
              color={props.color}
              disableAlpha={props.solid === true}
              onChange={(result) => {
                previewed.current = true;
                props.onPreview(fromPicker(result, props.solid === true));
              }}
            />
          </div>
        </>
      )}
    </div>
  );
}
